import dayjs from "@/helpers/dayjs";
import isBetween from "dayjs/plugin/isBetween";

const CONFIG = {
  NOW: dayjs(),
  AVAILABLE_DATE_RANGE: 30, // 선택가능한 "날짜", 출/도착지 상관없이 오늘로부터 +30일 까지
  AVAILABLE_HOUR_LIST: Array.from({ length: 24 }, (_, i) =>
    i.toString().padStart(2, "0")
  ), // 선택가능한 "시간" (00~24)
  AVAILABLE_MINUTE_LIST: ["00", "15", "30", "45"], // 선택가능한 "분"
  INITIAL_ADD_MINUTE: 60, // 초기값으로 추가되는 시간 (분)
  CEIL_MINUTE: 15, // 분단위 올림에서 사용하는 값 (분)
  WAYPOINT_ADD_MINUTE: 10, // 경유지당 추가되는 시간 (분)
  DURATION_ADD_RATIO: 0.4, // 네비게이션 소요시간에 추가되는 비율
  DURATION_MAX_MINUTE: 120, // 네비게이션 소요시간 최댓값 (분)
  DURATION_MIN_MINUTE: 20, // 추가시간 최솟값 (분)
};

export default {
  data() {
    return {
      now: dayjs().startOf("minute"),
      // 유저가 선택한 픽업시간
      userPickupTime: {
        origin: null,
        destination: null,
      },
      options: {
        duration: 0, // 네비게이션 소요 시간
        waypoints: 0, // 경유지 갯수
      },
      config: {
        closed: null, // 주문가능여부
        startTime: null, // 주문 가능한 가장 빠른 시간
        endTime: null, // 픽업 가능한 최대 시간
        isOrdertime: true, // 접수 가능한 시간인지 확인
        afterConfigTime: null,
      },
    };
  },
  created() {
    this._getConfigInfo();
    this.handleUpdateNowTime();
  },
  computed: {
    isLoaded() {
      return this.config.startTime && this.config.endTime;
    },
    /** 도착지 최소선택 시간 */
    _getDestinationMinPickupTime() {
      const origin = this.userPickupTime.origin;
      const { duration, waypoints } = this.options;

      if (!origin || !duration) return null; // 출발지 픽업시간 또는 요금계산이 되어 있지 않은 경우 제외
      const waypointMinute = waypoints * CONFIG.WAYPOINT_ADD_MINUTE; // 경유지 시간
      const durationMinute = this._getMsToMinute(duration); // 네비게이션 시간
      const finalTime = dayjs(origin).add(
        waypointMinute + durationMinute + this._calcAddedTime(durationMinute),
        "m"
      );
      return this._getRoundedMinute(finalTime);
    },

    /** 선택가능한 출발지 픽업시간 */
    getAvailableOriginPickupTime() {
      const userTime = this.userPickupTime.origin;
      const minTime = this._getOriginMinMaxTime();
      return {
        date: this._getAvailableDate(minTime),
        hour: this._getAvailableHour(minTime, userTime),
        minute: this._getAvailableMinute(minTime, userTime),
      };
    },
    /** 선택가능한 도착지 픽업시간 */
    getAvailableDestinationPickupTime() {
      const minTime = this._getDestinationMinPickupTime;
      const userTime = this.userPickupTime.destination;

      return {
        date: this._getAvailableDate(minTime),
        hour: this._getAvailableHour(minTime, userTime),
        minute: this._getAvailableMinute(minTime, userTime),
      };
    },
  },
  methods: {
    /**
     * 유저가 선택한 출/도착지 픽업시간 업데이트
     * @param { {origin: dayjs, destination: dayjs} } value
     */
    handleUpdatePickupTime(value) {
      const pickup = this.userPickupTime;

      this.$set(pickup, "origin", value.origin ?? pickup.origin);
      this.$set(pickup, "destination", value.destination ?? pickup.destination);
    },
    /**
     * 경유지 갯수 및 소요시간 업데이트
     * @param { {duration: number, waypoints: number} } value
     */
    handleUpdatePickupTimeOptions(value) {
      const options = this.options;

      this.$set(options, "duration", value.duration ?? options.duration);
      this.$set(options, "waypoints", value.waypoints ?? options.waypoints);
    },

    /**
     * 사용가능한 일자를 반환, 출/도착지 동일
     * @param {dayjs} time
     */
    _getAvailableDate(minTime) {
      if (!minTime)
        // 시간이 없는 경우, 오늘 날짜를 기준으로 min / max 설정
        return {
          min: this.now.format("YYYY-MM-DD"),
          max: this.now
            .add(CONFIG.AVAILABLE_DATE_RANGE, "d")
            .format("YYYY-MM-DD"),
        };

      return {
        min: minTime.format("YYYY-MM-DD"),
        max: this.now
          .add(CONFIG.AVAILABLE_DATE_RANGE, "d")
          .format("YYYY-MM-DD"), // 도착지일 경우, 유저가 선택한 시간이 어떻게 되던 선택할 수 있는 최대일은 고정입니다.
      };
    },
    /**
     * 사용가능한 시간을 반환
     * @param {dayjs} time
     */
    _getAvailableHour(minTime, userTime) {
      if (!minTime) return []; // 시간이 없는경우 선택 불가
      userTime = userTime || minTime;
      if (userTime.isAfter(minTime, "day")) return CONFIG.AVAILABLE_HOUR_LIST;

      const hour = minTime.hour();
      let hourList = CONFIG.AVAILABLE_HOUR_LIST;

      return hourList.slice(hour);
    },
    /**
     * 사용가능한 분을 반환
     * @param {dayjs} minTime
     * @param {dayjs} userTime
     */
    _getAvailableMinute(minTime, userTime) {
      //날짜, 시간 지났을때 로직 통합
      if (!minTime) return []; // 시간이 없는경우 선택 불가
      if (!userTime) userTime = minTime; // 분 리스트 렌더링을 위해, 유저가 선택한 시간이 없는 경우 minTime을 기준으로 렌더링합니다.
      const isToday = userTime.isSame(minTime, "day");
      const isHour = userTime.isSame(minTime, "hour");
      const minute = minTime.minute().toString().padStart(2, "0");

      const parseMinute = [
        minute,
        ...CONFIG.AVAILABLE_MINUTE_LIST.filter((m) => m > minute),
      ];

      return !isToday || !isHour ? CONFIG.AVAILABLE_MINUTE_LIST : parseMinute;
    },

    /**
     * 분단위 올림작업
     * @param {dayjs} time
     */
    _getCeilMinute(time) {
      // 10, 20, 30 등 분이 10단위로 떨어지는 경우 무조건 올림처리를 위해 1을 더합니다.
      // 19, 29, 39등의 경우는 1을 더했을 때 20, 30, 40이 되기 때문에 올림처리가 되지 않습니다.
      const parseMinute = time.minute() + 1;

      return time
        .startOf("hour")
        .minute(
          Math.ceil(parseMinute / CONFIG.CEIL_MINUTE) * CONFIG.CEIL_MINUTE
        );
    },

    /**
     * 밀리세컨드를 분으로 변환
     * @param {number} ms
     */
    _getMsToMinute(ms) {
      return Math.floor(ms / 60000);
    },

    /**
     * 네비게이션 소요시간에 더해야 하는 추가시간 계산 로직
     * 20분과 네비게이션 * 0.4 값 중 더 큰값을 반환
     * 단, 추가시간은 최대 120분
     * @param {number} minute
     */
    _calcAddedTime(minute) {
      const durationRatio = minute * CONFIG.DURATION_ADD_RATIO;

      return Math.min(
        Math.max(durationRatio, CONFIG.DURATION_MIN_MINUTE),
        CONFIG.DURATION_MAX_MINUTE
      );
    },

    async _getConfigInfo() {
      dayjs.extend(isBetween);
      const config = await this.$store.dispatch("api_get_config");

      this.config.startTime = this.now
        .set("hour", config.businessHours.startTime.substring(0, 2))
        .set("minute", config.businessHours.startTime.substring(2, 4));

      this.config.endTime = this.now
        .set("hour", config.businessHours.endTime.substring(0, 2))
        .set("minute", config.businessHours.endTime.substring(2, 4));

      this.config.closed = config.closed;

      this.$set(
        this.config,
        "isOrdertime",

        !config.businessHours.activated ||
          (config.businessHours.activated &&
            dayjs(this.now).isBetween(
              this.config.startTime,
              this.config.endTime
            ))
      );

      this.config.afterConfigTime = this._getAfterConfigTime(
        config.businessHours.delayTime
      );
    },

    //출발지 선택 가능한 가장 빠른 시간
    _getOriginMinMaxTime() {
      const minTime = this._getCeilMinute(
        this.now.add(CONFIG.INITIAL_ADD_MINUTE, "m")
      );

      if (this.config.isOrdertime) {
        return minTime;
      }
      return this.config.afterConfigTime;
    },

    //asap, 시간 초기화 시 현재시간 업데이트
    handleUpdateNowTime() {
      this.now = dayjs().startOf("minute");
    },

    //10분단위로 올림 (10:01 -> 10:00)
    _getRoundedMinute(time) {
      const roundedMinutes = Math.ceil(time.minute() / 10) * 10;
      const roundedTime = time.minute(roundedMinutes).startOf("minute");

      return roundedTime;
    },

    //주문접수 가능 시간이 아닐 시 주문 가능한 가장 빠른 시간 계산
    _getAfterConfigTime(time) {
      const afterConfigTime = this.config.startTime
        .add(time.substring(0, 2), "hour")
        .add(time.substring(2, 4), "minute");
      if (dayjs(this.now).isBefore(this.config.startTime))
        return afterConfigTime;
      return afterConfigTime.add(1, "day");
    },
  },
};
